import React, { useState, useEffect } from 'react';
import { Container, Typography, Card, CardContent, Grid, TextField, Button, Stack, Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { api } from '../api';

const ConfigurationPage = ({ state, config, refreshData }) => {
    const [formState, setFormState] = useState(config || {});
    const [isSaving, setIsSaving] = useState(false);
    const [isFetching, setIsFetching] = useState('');
    const [isSyncing, setIsSyncing] = useState('');
    const [syncKeywordId, setSyncKeywordId] = useState('');

    useEffect(() => {
        setFormState(config || {});
        if (state.keywords && state.keywords.length > 0 && !syncKeywordId) {
            setSyncKeywordId(state.keywords[0].id);
        }
    }, [config, state.keywords, syncKeywordId]);

    const handleChange = (e) => {
        setFormState(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await api.post(`/configurations`, formState);
            alert('Configuration saved successfully!');
            await refreshData();
        } catch (err) {
            alert('Failed to save configuration.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleFetch = async (platform) => {
        setIsFetching(platform);
        try {
            await api.post(`/mentions/fetch-now/${platform}`);
            alert(`Fetch triggered for ${platform}. Refreshing data...`);
            await refreshData();
        } catch (err) {
            alert(`Failed to trigger fetch for ${platform}.`);
        } finally {
            setIsFetching('');
        }
    };
    
    const handleSync = async (platform) => {
        if (!syncKeywordId) {
            return alert('Please select a keyword to sync the data to.');
        }
        setIsSyncing(platform);
        try {
            const response = await api.post(`/utility/sync-${platform}`, { keywordId: syncKeywordId });
            alert(response.data.message);
            await refreshData();
        } catch (error) {
            alert('An error occurred during the sync process.');
        } finally {
            setIsSyncing('');
        }
    };

    return (
        <Container maxWidth="md">
            <Typography variant="h4" gutterBottom>Configuration</Typography>
            
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6">API Keys & Settings</Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
                        <Grid item xs={12} sm={9}><TextField fullWidth label="X (Twitter) Bearer Token" name="twitterBearerToken" value={formState.twitterBearerToken || ''} onChange={handleChange} type="password" /></Grid>
                        <Grid item xs={12} sm={3}><Button fullWidth variant="outlined" onClick={() => handleFetch('twitter')} disabled={!!isFetching}>{isFetching === 'twitter' ? '...' : 'Fetch X'}</Button></Grid>
                        
                        <Grid item xs={12} sm={9}><TextField fullWidth label="YouTube API Key" name="youtubeApiKey" value={formState.youtubeApiKey || ''} onChange={handleChange} type="password" /></Grid>
                        <Grid item xs={12} sm={3}><Button fullWidth variant="outlined" onClick={() => handleFetch('youtube')} disabled={!!isFetching}>{isFetching === 'youtube' ? '...' : 'Fetch'}</Button></Grid>

                        <Grid item xs={12} sm={9}><TextField fullWidth label="Apify Token" name="apifyToken" value={formState.apifyToken || ''} onChange={handleChange} type="password" /></Grid>
                        <Grid item xs={12} sm={3}>
                            <Stack direction="row" spacing={1}>
                                <Button fullWidth size="small" variant="outlined" onClick={() => handleFetch('facebook')} disabled={!!isFetching}>FB</Button>
                                <Button fullWidth size="small" variant="outlined" onClick={() => handleFetch('instagram')} disabled={!!isFetching}>IG</Button>
                                <Button fullWidth size="small" variant="outlined" onClick={() => handleFetch('linkedin')} disabled={!!isFetching}>LI</Button>
                                <Button fullWidth size="small" variant="outlined" onClick={() => handleFetch('tiktok')} disabled={!!isFetching}>TT</Button>
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="NewsAPI.org API Key" 
                                name="newsApiKey" 
                                value={formState.newsApiKey || ''} 
                                onChange={handleChange} 
                                type="password" 
                                helperText="Required for the News page and cron job."
                            />
                        </Grid>
                        
                        <Grid item xs={12}><TextField fullWidth label="Facebook Page Access Token" name="facebookPageToken" value={formState.facebookPageToken || ''} onChange={handleChange} type="password" helperText="Required for fetching data from your managed Facebook Pages." /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Facebook App Secret" name="facebookAppSecret" value={formState.facebookAppSecret || ''} onChange={handleChange} type="password" helperText="Required for Facebook Graph API calls." /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="LinkedIn Cookie ('li_at')" name="linkedinCookie" value={formState.linkedinCookie || ''} onChange={handleChange} type="password" multiline rows={2} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="SerpApi API Key (for Trends)" name="serpApiKey" value={formState.serpApiKey || ''} onChange={handleChange} type="password" /></Grid>
                    </Grid>
                </CardContent>
            </Card>
            
             <Card sx={{mb: 3}}><CardContent>
                <Typography variant="h6">Email Alert Settings (SMTP)</Typography>
                 <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="SMTP Host" name="smtpHost" value={formState.smtpHost || ''} onChange={handleChange} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="SMTP Port" name="smtpPort" value={formState.smtpPort || ''} onChange={handleChange} type="number" /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="SMTP User" name="smtpUser" value={formState.smtpUser || ''} onChange={handleChange} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="SMTP Password" name="smtpPass" value={formState.smtpPass || ''} onChange={handleChange} type="password" /></Grid>
                 </Grid>
             </CardContent></Card>

             <Card>
                <CardContent>
                    <Typography variant="h6">Data Utilities</Typography>
                    <Stack spacing={2} sx={{mt: 2}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                            <FormControl size="small" sx={{minWidth: 200}}>
                                <InputLabel>Sync to Keyword</InputLabel>
                                <Select value={syncKeywordId} label="Sync to Keyword" onChange={e => setSyncKeywordId(e.target.value)}>
                                    {(state.keywords || []).map(kw => (
                                        <MenuItem key={kw.id} value={kw.id}>{kw.term}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="outlined" color="secondary" onClick={() => handleSync('facebook')} disabled={!!isSyncing || !syncKeywordId}>
                                {isSyncing === 'facebook' ? 'Syncing...' : 'Sync Facebook Data'}
                            </Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            This syncs all raw Facebook data from MongoDB to your main database, associating new mentions with the selected keyword.
                        </Typography>
                    </Stack>
                </CardContent>
             </Card>

            <Button fullWidth variant="contained" onClick={handleSave} disabled={isSaving} sx={{mt: 3}}>{isSaving ? 'Saving...' : 'Save All Configurations'}</Button>
        </Container>
    );
};

export default ConfigurationPage;